import { useTranslation } from "react-i18next";

const priceData = [
  { label: "Jan", normal: 82, crisis: 92 },
  { label: "Feb", normal: 85, crisis: 98 },
  { label: "Mar", normal: 80, crisis: 110 },
  { label: "Apr", normal: 88, crisis: 128 },
  { label: "May", normal: 84, crisis: 145 },
  { label: "Jun", normal: 86, crisis: 162 },
];
const maxVal = 170;

const newsData = [
  { date: "Mar 2026", source: "Reuters Energy", tagKey: "market.tag.breaking", newsKey: "market.news1" },
  { date: "Mar 2026", source: "Bloomberg", tagKey: "market.tag.deal", newsKey: "market.news2" },
  { date: "Feb 2026", source: "CNBC", tagKey: "market.tag.surge", newsKey: "market.news3" },
  { date: "Feb 2026", source: "Arab News", tagKey: "market.tag.agreement", newsKey: "market.news4" },
];

const metricsData = [
  { key: "market.metric1", value: "$142.30", change: "+74%", up: true },
  { key: "market.metric2", value: "$14.80", change: "+112%", up: true },
  { key: "market.metric3", value: "$18.50/bbl", change: "+340%", up: true },
  { key: "market.metric4", changeKey: "market.metric4change", value: "47 days", up: null },
];

export default function MarketAnalysis() {
  const { t } = useTranslation();

  return (
    <section id="market" className="bg-[#0d0d0d] py-24 px-6">
      <div className="max-w-7xl mx-auto">

        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
            <span className="text-[#c9a84c] text-xs font-bold tracking-[4px] uppercase">{t("market.badge")}</span>
            <div className="w-8 h-[2px] bg-[#c9a84c]" />
          </div>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {t("market.title1")} <span className="text-[#c9a84c]">{t("market.title2")}</span>
          </h2>
          <p className="text-white/60 max-w-xl mx-auto text-base">{t("market.desc")}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {metricsData.map((m) => (
            <div key={m.key} className="bg-white/5 border border-white/10 rounded-xl p-5">
              <p className="text-white/40 text-xs mb-2 leading-tight">{t(m.key)}</p>
              <p className="text-white font-black text-xl mb-1">{m.value}</p>
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                m.up === true ? "bg-green-500/20 text-green-400" :
                m.up === false ? "bg-red-500/20 text-red-400" :
                "bg-[#c9a84c]/20 text-[#c9a84c]"
              }`}>
                {m.changeKey ? t(m.changeKey) : m.change}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
          <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-black text-base">{t("market.chartTitle")}</h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-white/30" />
                  <span className="text-white/40 text-xs">{t("market.normal")}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-[#c9a84c]" />
                  <span className="text-white/40 text-xs">{t("market.crisis")}</span>
                </div>
              </div>
            </div>
            <div className="flex items-end gap-2 h-48">
              {priceData.map((d) => (
                <div key={d.label} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-end gap-1 h-40 justify-center">
                    <div className="flex-1 bg-white/20 rounded-t-sm" style={{ height: `${(d.normal / maxVal) * 100}%` }} />
                    <div className="flex-1 bg-[#c9a84c] rounded-t-sm" style={{ height: `${(d.crisis / maxVal) * 100}%` }} />
                  </div>
                  <span className="text-white/30 text-[10px]">{d.label}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center">
              <span className="text-[#c9a84c] font-black text-2xl">+97%</span>
              <span className="text-white/40 text-sm ml-2">{t("market.avgIncrease")}</span>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-7">
            <h3 className="text-white font-black text-base mb-6">{t("market.newsTitle")}</h3>
            <div className="space-y-4">
              {newsData.map((n) => (
                <div key={n.newsKey} className="border-b border-white/5 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-[10px] bg-[#c9a84c]/20 text-[#c9a84c] px-2 py-0.5 rounded-full font-bold">{t(n.tagKey)}</span>
                    <span className="text-white/30 text-xs">{n.date} · {n.source}</span>
                  </div>
                  <p className="text-white/80 text-sm leading-relaxed font-medium">{t(n.newsKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/ccd24d8efc7f3bea0d703464734c8f4e.jpg"
            alt="Oil tanker at sea"
            className="w-full h-[300px] object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0b0b0b]/90 via-[#0b0b0b]/50 to-transparent" />
          <div className="absolute inset-0 flex items-center px-10">
            <div className="max-w-xl">
              <p className="text-[#c9a84c] font-bold text-xs uppercase tracking-widest mb-3">{t("market.strategic.badge")}</p>
              <h3 className="text-white font-black text-2xl md:text-3xl mb-4 leading-tight">
                {t("market.strategic.title").replace("100%", "")}
                <span className="text-[#c9a84c]">100%</span>
                {t("market.strategic.title").split("100%")[1] ?? ""}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">{t("market.strategic.desc")}</p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
